import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import axios from 'axios';
import { Layers, Download, ArrowRight, Eye, Sparkles } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SharePage = () => {
  const { shareId } = useParams();
  const [share, setShare] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchShare();
  }, [shareId]);

  const fetchShare = async () => {
    try {
      const response = await axios.get(`${API}/api/share/${shareId}`);
      setShare(response.data);
    } catch (err) {
      setError('This share link is no longer available');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!share?.image_base64) return;
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${share.image_base64}`;
    link.download = `mockuppro-${shareId}.png`;
    link.click();
  };

  const getTypeLabel = (type) => {
    const labels = {
      'image_generate': 'AI Generated Image',
      'mockup_generate': 'Product Mockup',
      'background_generate': 'Custom Background',
      'image_to_prompt': 'Prompt',
      'prompt_enhance': 'Enhanced Prompt'
    };
    return labels[type] || 'Creation';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-full max-w-2xl px-6">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-muted rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Layers className="w-8 h-8 text-muted-foreground" />
            </div>
            <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Share Not Found
            </h2>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Link to="/">
              <Button>Go to MockupPro</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="share-page">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-foreground rounded-lg flex items-center justify-center">
              <Layers className="w-5 h-5 text-background" />
            </div>
            <span className="font-semibold text-xl tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              MockupPro
            </span>
          </Link>
          <Link to="/auth?mode=register">
            <Button data-testid="share-cta-btn">
              Start Creating <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Title & Meta */}
          <div className="text-center">
            <span className="inline-flex items-center gap-1 text-xs font-medium bg-accent px-3 py-1 rounded-full mb-4">
              <Sparkles className="w-3 h-3" />
              {getTypeLabel(share.type)}
            </span>
            <h1 
              className="text-2xl sm:text-3xl font-bold mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="share-title"
            >
              {share.title}
            </h1>
            <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {share.views} views
              </span>
              <span>Created with MockupPro</span>
            </div>
          </div>

          {/* Image */}
          {share.image_base64 && (
            <Card className="overflow-hidden">
              <CardContent className="p-0">
                <div className="relative bg-muted/30">
                  <img 
                    src={`data:image/png;base64,${share.image_base64}`}
                    alt={share.title}
                    className="w-full h-auto"
                    data-testid="shared-image"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              variant="outline" 
              onClick={handleDownload}
              data-testid="download-shared-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Image
            </Button>
            <Link to="/auth?mode=register">
              <Button className="w-full sm:w-auto" data-testid="create-own-btn">
                <Sparkles className="w-4 h-4 mr-2" />
                Create Your Own
              </Button>
            </Link>
          </div>

          {/* CTA Section */}
          <Card className="bg-muted/30 border-dashed">
            <CardContent className="p-8 text-center">
              <h3 
                className="text-xl font-semibold mb-2"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Want to create images like this?
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                MockupPro combines AI image generation, mockups, and backgrounds into one powerful workflow. 
                Start with 50 free credits.
              </p>
              <Link to="/auth?mode=register">
                <Button size="lg" data-testid="share-signup-btn">
                  Start Free Trial <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </motion.div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-foreground rounded flex items-center justify-center">
              <Layers className="w-4 h-4 text-background" />
            </div>
            <span className="font-medium">MockupPro</span>
          </div>
          <p className="text-sm text-muted-foreground">
            AI-powered tools for creators. Start free today.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SharePage;
